import { useEffect, useId, useRef } from 'react';
import { StatusDotPill } from '@/shared';
import { formatChileanDateTime } from '@/lib';
import { STATUS_PILL } from './intakeStatus';

const fmtDateTime = (iso) => formatChileanDateTime(iso) || '—';

export const EMPTY_PROMPT =
  'Selecciona un pedido para ver el mensaje y las acciones.';

// Detail panel of the selected WhatsApp intake. Until the API has a GET by id
// (A1), it shows only what the queue row carries.
//
// `kind` is one of:
//   empty    no ?id= (desktop only): invite to pick a pedido
//   loading  the queues are still loading and the id is not among the rows
//   error    a queue failed to load and the id is not among the rows
//   missing  the id is not among the loaded rows
//   found    `row` is the selected intake
export default function WhatsAppIntakeDetail({
  panelId,
  intakeId,
  kind,
  row,
  mobile = false,
  onClose,
  busy = false,
  preflightOpen = false,
  accepting = false,
  onAccept,
  onReject,
  canLoadMore = false,
  loadingMore = false,
  onLoadMore,
  onRetry,
}) {
  const headingId = useId();
  const headingRef = useRef(null);

  // Focus follows the selection: on open, on switching pedidos, and when the
  // row turns up after the queues load.
  useEffect(() => {
    if (kind !== 'empty') headingRef.current?.focus();
  }, [intakeId, kind]);

  // Once the pedido is decided, Aceptar and Rechazar go away. If focus was on
  // one of them (or on the body, where a disabled button leaves it), it moves
  // to the title instead of being lost.
  const canDecide = kind === 'found' && row?.status === 'pending';
  const decidableRef = useRef({ intakeId, canDecide });
  useEffect(() => {
    const prev = decidableRef.current;
    decidableRef.current = { intakeId, canDecide };
    if (prev.intakeId !== intakeId || !prev.canDecide || canDecide) return;
    const active = document.activeElement;
    if (!active || active === document.body) headingRef.current?.focus();
  }, [intakeId, canDecide]);

  if (kind === 'empty') {
    return (
      <section
        id={panelId}
        className="pli-detail pli-detail--empty"
        aria-label="Detalle del pedido"
      >
        <p className="pli-detail__prompt">{EMPTY_PROMPT}</p>
      </section>
    );
  }

  // Esc closes the panel while focus is inside it, unless the Aceptar or
  // Rechazar confirmation is open: that Esc belongs to the dialog.
  const onKeyDown = (e) => {
    if (e.key !== 'Escape' || e.repeat || e.defaultPrevented || preflightOpen)
      return;
    e.preventDefault();
    onClose();
  };

  const close = mobile ? (
    <button
      type="button"
      className="pli-detail__back"
      aria-label="Volver a WhatsApp"
      onClick={onClose}
    >
      <span aria-hidden="true">←</span> WhatsApp
    </button>
  ) : (
    <button
      type="button"
      className="pli-detail__close"
      aria-label="Cerrar detalle"
      onClick={onClose}
    >
      <span aria-hidden="true">×</span>
    </button>
  );

  const heading = (text) => (
    <h2
      id={headingId}
      ref={headingRef}
      tabIndex={-1}
      className="pli-detail__title"
    >
      {text}
    </h2>
  );

  if (kind !== 'found' || !row) {
    return (
      <section
        id={panelId}
        className="pli-detail"
        aria-labelledby={headingId}
        aria-busy={kind === 'loading' ? 'true' : undefined}
        onKeyDown={onKeyDown}
      >
        <div className="pli-detail__bar">{close}</div>
        {kind === 'loading' ? (
          heading('Cargando pedido…')
        ) : kind === 'error' ? (
          <>
            {heading('No se pudieron cargar los pedidos.')}
            <div className="pli-detail__actions">
              <button type="button" className="plp-morebtn" onClick={onRetry}>
                Reintentar
              </button>
            </div>
          </>
        ) : canLoadMore ? (
          <>
            {heading('Este pedido no está entre los cargados.')}
            <p className="pli-detail__note">
              Puede estar en una página de la cola que todavía no se carga.
            </p>
            <div className="pli-detail__actions">
              <button
                type="button"
                className="plp-morebtn"
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {loadingMore ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          </>
        ) : (
          heading('No encontramos este pedido en las colas.')
        )}
      </section>
    );
  }

  const st = STATUS_PILL[row.status] ?? STATUS_PILL.pending;
  const actionsDisabled = preflightOpen || busy;

  return (
    <section
      id={panelId}
      className="pli-detail"
      aria-labelledby={headingId}
      onKeyDown={onKeyDown}
    >
      <div className="pli-detail__bar">{close}</div>
      <header className="pli-detail__head">
        {heading(row.sender || 'Sin teléfono')}
        <StatusDotPill status={st.tone} label={st.label} />
        <dl className="pli-detail__dates">
          <div className="plp-cf">
            <dt className="plp-cf__l">Recibido</dt>
            <dd className="plp-cf__v">{fmtDateTime(row.createdAt)}</dd>
          </div>
          <div className="plp-cf">
            <dt className="plp-cf__l">Actualizado</dt>
            <dd className="plp-cf__v">{fmtDateTime(row.updatedAt)}</dd>
          </div>
        </dl>
      </header>

      <div className="pli-detail__actions">
        {canDecide && (
          <div className="plp-actions">
            <button
              type="button"
              className="plp-rowbtn"
              disabled={actionsDisabled}
              onClick={() => onAccept(row)}
            >
              {busy && accepting ? '…' : 'Aceptar'}
            </button>
            <button
              type="button"
              className="plp-rowbtn plp-rowbtn--ghost"
              disabled={actionsDisabled}
              onClick={() => onReject(row)}
            >
              Rechazar
            </button>
          </div>
        )}
        <a
          className="pli-kapso"
          href={row.kapsoInboxUrl}
          target="_blank"
          rel="noreferrer"
        >
          Abrir en Kapso
        </a>
      </div>

      <div className="pli-detail__msg">
        <h3 className="plp-detail__h">Mensaje</h3>
        <p className="pli-detail__text">
          {row.preview || 'El último mensaje no trae texto.'}
        </p>
        <p className="pli-detail__note">Vista previa, hasta 280 caracteres.</p>
      </div>

      <p className="pli-detail__note">
        El hilo completo está en Kapso. Ábrelo ahí para leer la conversación.
      </p>
    </section>
  );
}
